import { type FC, useState } from 'react';

import AddressFinder, {
  addressFinderSchema,
  defaultValues as addressFinderDefaultValues,
} from '@eneco/components/AddressFinder/AddressFinder';
import { useAddressFinderQueryStringDefaultValues } from '@eneco/components/AddressFinder/hooks/useAddressFinderQueryStringDefaultValues';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { authenticateUser } from '@eneco/customer-insights';
import { useIsLargeBusinessCheck } from '@eneco/hooks/address';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRedirect } from '@eneco/hooks/redirect';
import { Box, Button, Form, Stack } from '@eneco/sparky';
import { SmeAddressFinderRendering } from '@sitecore/types/SmeAddressFinder';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';

import { EnergyUsagePurposeSelector } from './EnergyUsagePurpose/EnergyUsagePurposeSelector';

interface FormFields {
  city?: string;
  houseNumber: string;
  houseNumberSuffix?: string;
  postalCode: string;
  street?: string;
  energyUsagePurpose?: string;
}

const withExternalLinkType = <TLink extends Record<string, unknown> | undefined>(link: TLink): TLink => {
  if (!link) return link;
  // Set the linktype to external to force a full page reload, that is required when redirecting between different sites (e.g. MKB to Grootzakelijk)
  return { ...link, linktype: 'external' } as TLink;
};

const SmeAddressFinder: FC<SmeAddressFinderRendering> = ({ fields }) => {
  const addressFinderQueryStringDefaultValues = useAddressFinderQueryStringDefaultValues();
  const redirect = useRedirect();
  const [isSuffixRequired, setIsSuffixRequired] = useState(false);
  const {
    buttonLink,
    energyUsagePurposeList,
    energyUsagePurposeDefaultOption,
    energyUsagePurposeRedirectLink,
    enterpriseRedirectLink,
  } = fields;

  // TODO: needs to be updated to use the Sitecore error message for the energyUsagePurpose field
  const formSchema = yup.object({
    ...addressFinderSchema(isSuffixRequired).fields,
    energyUsagePurpose: yup
      .string()
      .required("Maak een keuze tussen 'Huishouden' of 'Zakelijk'")
      .notOneOf(['none'], "Maak een keuze tussen 'Huishouden' of 'Zakelijk'"),
  });

  const formMethods = useForm<FormFields>({
    defaultValues: {
      ...addressFinderDefaultValues,
      ...addressFinderQueryStringDefaultValues,
      energyUsagePurpose: energyUsagePurposeDefaultOption?.value ?? 'none',
    },
    mode: 'onBlur',
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const { handleSubmit, control, watch } = formMethods;

  const { energyUsagePurpose, postalCode, houseNumber, houseNumberSuffix } = watch();
  const isLargeBusiness = useIsLargeBusinessCheck({ postalCode, houseNumber, houseNumberSuffix });

  const onSubmit = ({ postalCode, houseNumber, houseNumberSuffix }: FormFields) => {
    // If "Huishouden" (home) is selected, use redirect link, otherwise use normal button link
    const linkToUse =
      energyUsagePurpose === 'home' && energyUsagePurposeRedirectLink?.value?.href
        ? energyUsagePurposeRedirectLink.value
        : buttonLink?.value;

    redirect({
      queryParams: { postalCode: postalCode?.replace?.(' ', ''), houseNumber, houseNumberSuffix },
      link: withExternalLinkType(linkToUse),
    });
  };

  const handleEnterpriseRedirect = ({ postalCode, houseNumber, houseNumberSuffix }: FormFields) => {
    authenticateUser(`zip: ${[postalCode, houseNumber, houseNumberSuffix].join(' ')}`);
    redirect({
      queryParams: { postalCode: postalCode?.replace?.(' ', ''), houseNumber, houseNumberSuffix },
      link: withExternalLinkType(enterpriseRedirectLink?.value),
    });
  };

  const customLabels = {
    houseNumber: fields.houseNumberFormField,
    houseNumberSuffix: fields.houseNumberSuffixFormField,
    postalCode: fields.postalCodeFormField,
    notFoundErrorNotification: fields.notFoundErrorNotification,
    serverErrorNotification: fields.serverErrorNotification,
  };

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <AddressFinder
          customLabels={customLabels}
          isSuffixRequired={isSuffixRequired}
          setIsSuffixRequired={setIsSuffixRequired}
        />

        <EnergyUsagePurposeSelector control={control} energyUsagePurposeList={energyUsagePurposeList} direction="row" />

        {buttonLink?.value?.href && (
          <Box paddingTop="6">
            {isLargeBusiness ? (
              <TrackedDialog
                title={fields.enterpriseDialog?.value?.title}
                trigger={
                  <Button size="regular" type="button">
                    {buttonLink?.value?.text}
                  </Button>
                }
                width="regular">
                <Box paddingBottom="6">
                  <RichText html={fields.enterpriseDialog?.value?.content} />
                </Box>
                <Stack direction="row" gap={5}>
                  <Button
                    action="primary"
                    type="button"
                    size="regular"
                    onClick={() => handleEnterpriseRedirect({ postalCode, houseNumber, houseNumberSuffix })}>
                    {fields.enterpriseDialog?.value?.submitButtonText}
                  </Button>
                  <Button
                    action="secondary"
                    size="regular"
                    type="button"
                    onClick={() => onSubmit({ postalCode, houseNumber, houseNumberSuffix })}>
                    {fields.enterpriseDialog?.value?.cancelButtonText}
                  </Button>
                </Stack>
              </TrackedDialog>
            ) : (
              <Button size="regular" type="submit">
                {buttonLink?.value?.text}
              </Button>
            )}
          </Box>
        )}
      </Form>
    </FormProvider>
  );
};

export { SmeAddressFinder };
export default SmeAddressFinder;
